import { BeneficiaryModifiedDiscrepancy, DeepNullablePartial, DeletedBeneficiary, ErrorCode } from '@approvalmax/types';
import { toast } from '@approvalmax/ui/src/components';
import { isApiError } from '@approvalmax/utils/src/helpers/error/isApiError';
import { useMutation } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { backend, domain, stateTree } from 'modules/data';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { amplitudeService } from 'services/amplitude';
import { notificationService } from 'services/notification';
import { routingService } from 'services/routing';
import { usePublishRequest } from 'shared/data/webApp/v1';
import { hasChangesState } from 'shared/states';
import { getPath, Path } from 'urlBuilder';

import { useEditRequestForm } from '../../../hooks/useEditRequestForm';
import { getEditCommands, validateRequest } from '../../../selectors/requestSelectors';
import { getXeroAirwallexBatchPaymentRequestTransfer } from '../../../selectors/xero';
import { messages } from './XeroAirwallexBatchPaymentForm.messages';

export const useSaveAndSubmitMutation = (
    xeroAirwallexBatchPayment: domain.XeroAirwallexBatchPayment,
    request: domain.XeroAirwallexBatchPaymentRequest,
    setBillInvoiceRequestIdsWithExceededAmount: (billInvoiceRequestIdsWithExceededAmount: string[]) => void,
    setBillInvoiceRequestIdsWithError: (billInvoiceRequestIdsWithError: string[]) => void,
    setDeletedBeneficiariesData: (changedBeneficiariesData: DeletedBeneficiary[] | null) => void,
    setChangedBeneficiariesData: (changedBeneficiariesData: BeneficiaryModifiedDiscrepancy[] | null) => void
) => {
    const commands = useSelector(getEditCommands);
    const setHasChanges = useSetAtom(hasChangesState);

    const { mutateAsync: publishRequest, isLoading: isLoadingEditRequest } = usePublishRequest();
    const { mutateAsync: editRequest, isLoading: isLoadingPublishRequest } = useEditRequestForm();

    const { mutate } = useMutation(async (transfer: DeepNullablePartial<backend.transfers.RequestEditTransfer>) => {
        setBillInvoiceRequestIdsWithExceededAmount([]);
        setBillInvoiceRequestIdsWithError([]);
        setHasChanges(false);

        const shouldPublish = !commands.saveAndSubmit.hidden && !commands.saveAndSubmit.disabled;

        try {
            await editRequest(
                {
                    body: transfer,
                },
                {
                    onSuccess: () => {
                        if (!shouldPublish) {
                            routingService.push(getPath(Path.request, request.id, request.companyId));
                        }
                    },
                }
            );

            if (shouldPublish) {
                await publishRequest(
                    {
                        body: {
                            requestId: request.id,
                            companyId: request.companyId,
                        },
                    },
                    {
                        onSuccess: () => {
                            amplitudeService.sendData('requests: create request', {
                                'request type': domain.IntegrationCode.XeroAirwallexBatchPayment.toLocaleLowerCase(),
                            });

                            toast.success(messages.saveAndSubmitRequestSuccess);
                            routingService.push(getPath(Path.request, request.id, request.companyId));
                        },
                    }
                );
            }
        } catch (error) {
            if (isApiError(error)) {
                switch (error.code) {
                    case ErrorCode.E4158_XERO_BILL_BATCH_PAYMENT_EXCEEDED_AMOUNT: {
                        const billInvoiceRequestIdsWithExceededAmount = error.billInvoiceRequestIds || [];

                        setBillInvoiceRequestIdsWithExceededAmount(billInvoiceRequestIdsWithExceededAmount);
                        break;
                    }

                    case ErrorCode.E4159_XERO_BILL_BATCH_PAYMENT_DIFFERENT_CURRENCY:
                    case ErrorCode.E4161_XERO_BILL_BATCH_PAYMENT_INCORRECT_STATUS: {
                        const billInvoiceRequestIdsWithError = error.billInvoiceRequestIds || [];

                        setBillInvoiceRequestIdsWithError(billInvoiceRequestIdsWithError);
                        break;
                    }

                    case ErrorCode.E7717_BENEFICIARY_WAS_EDITED_BEFORE_PUBLISH:
                        if (error.beneficiaryDiscrepancies) {
                            setChangedBeneficiariesData(error.beneficiaryDiscrepancies);
                        }

                        break;

                    case ErrorCode.E7718_BENEFICIARY_WAS_DELETED_BEFORE_PUBLISH:
                        if (error.deletedBeneficiaries) {
                            setDeletedBeneficiariesData(error.deletedBeneficiaries);
                        }

                        break;
                }
            }
        }
    });

    const saveAndSubmit = useCallback(
        (commentText?: string, newDetails?: domain.XeroAirwallexBatchPayment) => {
            const details = {
                ...request.details,
                ...xeroAirwallexBatchPayment,
                ...newDetails,
            };
            const state = window.ApprovalMax.app.getStore().getState() as stateTree.State;
            const requestWithBillBatchPayment = { ...request, details };
            const errors = validateRequest(state, requestWithBillBatchPayment);

            if (errors.length > 0) {
                errors.forEach((error) => {
                    notificationService.showErrorToast(error);
                });

                return;
            }

            const transfer = getXeroAirwallexBatchPaymentRequestTransfer(
                window.ApprovalMax.app.getStore().getState() as stateTree.State,
                requestWithBillBatchPayment,
                commentText
            );

            mutate(transfer);
        },
        [mutate, request, xeroAirwallexBatchPayment]
    );

    return { saveAndSubmit, isSaving: isLoadingEditRequest || isLoadingPublishRequest };
};
